import { Modal } from '@/components/admin/Modal';
import {
  SavedQueryDetailModal,
  SessionBadge,
  formatDateTime,
} from '@/components/admin/SavedQueryDetailModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import type {
  SavedQueryDeleteOrphansRequest,
  SavedQueryKind,
  SavedQueryListItem,
} from '@innermate/shared';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

interface FilterState {
  ownerEmail: string;
  kind: '' | SavedQueryKind;
  from: string;
  to: string;
}

const EMPTY: FilterState = { ownerEmail: '', kind: '', from: '', to: '' };

function toIsoOrUndefined(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

function formatRelative(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return `${sec}초 전`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}분 전`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}시간 전`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}일 전`;
    return formatDateTime(iso);
  } catch {
    return iso;
  }
}

function sqlPreview(sql: string, max = 100): string {
  const oneLine = sql.replace(/\s+/g, ' ').trim();
  return oneLine.length > max ? `${oneLine.slice(0, max)}…` : oneLine;
}

export function AdminSavedQueries() {
  const [draft, setDraft] = useState<FilterState>(EMPTY);
  const [filters, setFilters] = useState<FilterState>(EMPTY);
  const [detailOpen, setDetailOpen] = useState<SavedQueryListItem | null>(null);
  const [orphanOpen, setOrphanOpen] = useState(false);
  const [orphanResult, setOrphanResult] = useState<{
    deleted: number;
    capped: boolean;
  } | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const qc = useQueryClient();

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((prev) => ({ ...prev, ownerEmail: draft.ownerEmail }));
    }, 300);
    return () => clearTimeout(handle);
  }, [draft.ownerEmail]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      kind: draft.kind,
      from: draft.from,
      to: draft.to,
    }));
  }, [draft.kind, draft.from, draft.to]);

  const ownerEmail = filters.ownerEmail.trim() || undefined;
  const fromIso = useMemo(() => toIsoOrUndefined(filters.from), [filters.from]);
  const toIso = useMemo(() => toIsoOrUndefined(filters.to), [filters.to]);
  const kind = filters.kind || undefined;

  const query = useInfiniteQuery({
    queryKey: ['admin', 'saved-queries', ownerEmail, kind, fromIso, toIso],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.adminSavedQueries({
        ownerEmail,
        kind,
        from: fromIso,
        to: toIso,
        cursor: pageParam,
        limit: 50,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const total = query.data?.pages[0]?.total ?? 0;
  const rows = useMemo(() => query.data?.pages.flatMap((p) => p.queries) ?? [], [query.data]);

  const invalidateList = () => qc.invalidateQueries({ queryKey: ['admin', 'saved-queries'] });

  const deleteMut = useMutation({
    mutationFn: (args: { id: string; confirmActive: boolean }) =>
      api.adminDeleteSavedQuery(args.id, { confirmActive: args.confirmActive }),
  });

  const deleteOrphansMut = useMutation({
    mutationFn: (body: SavedQueryDeleteOrphansRequest) => api.adminDeleteOrphanSavedQueries(body),
    onSuccess: (data) => {
      setOrphanResult(data);
      invalidateList();
    },
    onError: (err) => {
      setMutationError(err instanceof ApiError ? err.message : '일괄 삭제 중 오류가 발생했습니다.');
    },
  });

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && query.hasNextPage && !query.isFetchingNextPage) {
          query.fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [query]);

  function reset() {
    setDraft(EMPTY);
    setFilters(EMPTY);
  }

  const filtersActive = Boolean(ownerEmail || kind || fromIso || toIso);

  return (
    <PageShell
      header={
        <>
          <PageHeader
            title="저장된 쿼리"
            count={total}
            description="LLM 이 실행하거나 저장한 모든 SQL 이 여기에 기록됩니다. 일반 사용자에게는 queryId 만 노출되고 원본은 관리자만 볼 수 있습니다."
            actions={
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setOrphanResult(null);
                    setMutationError(null);
                    setOrphanOpen(true);
                  }}
                >
                  고아 쿼리 일괄 삭제
                </Button>
                <Button variant="secondary" onClick={() => query.refetch()}>
                  새로고침
                </Button>
              </div>
            }
          />
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <FilterField label="소유자 이메일 (substring)">
              <input
                type="text"
                value={draft.ownerEmail}
                onChange={(e) => setDraft((s) => ({ ...s, ownerEmail: e.target.value }))}
                placeholder="hong@..."
                className="h-9 w-56 rounded-md border border-border bg-surface px-2 text-sm"
              />
            </FilterField>
            <FilterField label="유형">
              <select
                value={draft.kind}
                onChange={(e) =>
                  setDraft((s) => ({ ...s, kind: e.target.value as FilterState['kind'] }))
                }
                className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
              >
                <option value="">전체</option>
                <option value="db">DB</option>
                <option value="excel">Excel</option>
              </select>
            </FilterField>
            <FilterField label="시작">
              <input
                type="datetime-local"
                value={draft.from}
                onChange={(e) => setDraft((s) => ({ ...s, from: e.target.value }))}
                className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
              />
            </FilterField>
            <FilterField label="끝">
              <input
                type="datetime-local"
                value={draft.to}
                onChange={(e) => setDraft((s) => ({ ...s, to: e.target.value }))}
                className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
              />
            </FilterField>
            <Button variant="ghost" size="sm" onClick={reset}>
              초기화
            </Button>
          </div>
        </>
      }
    >
      {query.isError ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {query.error instanceof ApiError
            ? query.error.message
            : '저장된 쿼리를 불러오지 못했습니다.'}
        </div>
      ) : null}
      {mutationError ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {mutationError}
        </div>
      ) : null}

      <div className="rounded-2xl border border-border bg-surface overflow-hidden">
        <table className="w-full text-sm table-fixed">
          <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
            <tr>
              <th className="text-left font-medium px-4 py-3 w-[180px]">시각</th>
              <th className="text-left font-medium px-4 py-3 w-[200px]">소유자</th>
              <th className="text-left font-medium px-4 py-3 w-[90px]">유형</th>
              <th className="text-left font-medium px-4 py-3 w-[240px]">대상 / 대화</th>
              <th className="text-left font-medium px-4 py-3">SQL 미리보기</th>
              <th className="text-left font-medium px-4 py-3 w-[80px]">사용</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-foreground-muted py-10">
                  불러오는 중…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-foreground-muted py-10">
                  조건에 맞는 쿼리가 없습니다.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                // biome-ignore lint/a11y/useKeyWithClickEvents: 행 전체 클릭으로 상세 — 키보드 사용자는 행 안의 액션 버튼으로 접근
                <tr
                  key={r.id}
                  className="border-t border-border hover:bg-surface-muted/30 align-top cursor-pointer"
                  onClick={() => setDetailOpen(r)}
                >
                  <td className="px-4 py-3 whitespace-nowrap" title={formatDateTime(r.createdAt)}>
                    <div>{formatRelative(r.createdAt)}</div>
                    <div className="text-xs text-foreground-muted font-mono">
                      {formatDateTime(r.createdAt)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <div className="truncate">{r.ownerDisplayName ?? '—'}</div>
                      <div className="text-xs text-foreground-muted truncate">
                        {r.ownerEmail ?? ''}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block rounded bg-surface-muted px-2 py-0.5 text-xs font-mono uppercase">
                      {r.kind}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div>
                      {r.kind === 'db' ? (
                        <span title={r.dataSourceId ?? ''}>{r.dataSourceName ?? '—'}</span>
                      ) : (
                        <span title={r.fileId ?? ''}>{r.fileName ?? '—'}</span>
                      )}
                    </div>
                    <div className="mt-1">
                      <SessionBadge item={r} />
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] break-all">{sqlPreview(r.sql)}</td>
                  <td className="px-4 py-3 text-xs font-mono">
                    {r.useCount}
                    {r.lastUsedAt ? (
                      <div className="text-[10px] text-foreground-muted">
                        {formatRelative(r.lastUsedAt)}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div
        ref={sentinelRef}
        className="h-12 flex items-center justify-center text-xs text-foreground-muted"
      >
        {query.isFetchingNextPage
          ? '더 불러오는 중…'
          : query.hasNextPage
            ? '스크롤 시 자동 로딩'
            : rows.length > 0
              ? '— 끝 —'
              : ''}
      </div>

      {detailOpen ? (
        <SavedQueryDetailModal
          item={detailOpen}
          onClose={() => {
            setDetailOpen(null);
            setMutationError(null);
          }}
          onDeleted={() => {
            setDetailOpen(null);
            invalidateList();
          }}
          deleteMut={deleteMut}
        />
      ) : null}

      {orphanOpen ? (
        <OrphanBulkDeleteModal
          filtersActive={filtersActive}
          filterSnapshot={{
            ownerEmail,
            kind,
            from: fromIso,
            to: toIso,
          }}
          result={orphanResult}
          pending={deleteOrphansMut.isPending}
          onClose={() => {
            setOrphanOpen(false);
            setOrphanResult(null);
          }}
          onConfirm={(mode) =>
            deleteOrphansMut.mutate({
              mode,
              ownerEmail,
              kind,
              from: fromIso,
              to: toIso,
            })
          }
        />
      ) : null}
    </PageShell>
  );
}

function OrphanBulkDeleteModal({
  filtersActive,
  filterSnapshot,
  result,
  pending,
  onClose,
  onConfirm,
}: {
  filtersActive: boolean;
  filterSnapshot: {
    ownerEmail: string | undefined;
    kind: SavedQueryKind | undefined;
    from: string | undefined;
    to: string | undefined;
  };
  result: { deleted: number; capped: boolean } | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: (mode: 'filtered' | 'all') => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title="고아 쿼리 일괄 삭제"
      description="원본 대화가 이미 삭제된 저장 쿼리만 제거합니다."
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            닫기
          </Button>
          {result ? null : filtersActive ? (
            <>
              <Button variant="secondary" disabled={pending} onClick={() => onConfirm('all')}>
                전체 고아 삭제
              </Button>
              <Button variant="danger" disabled={pending} onClick={() => onConfirm('filtered')}>
                {pending ? '삭제 중…' : '현재 필터 범위만 삭제'}
              </Button>
            </>
          ) : (
            <Button variant="danger" disabled={pending} onClick={() => onConfirm('all')}>
              {pending ? '삭제 중…' : '전체 고아 삭제'}
            </Button>
          )}
        </div>
      }
    >
      {result ? (
        <div className="space-y-2 text-sm">
          <p>
            <strong>{result.deleted}</strong>건의 고아 쿼리를 삭제했습니다.
          </p>
          {result.capped ? (
            <p className="text-foreground-muted">
              한 번 호출 상한(1000건)에 걸려 일부만 처리되었습니다. 다시 실행하면 나머지를 이어서
              제거합니다.
            </p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          <p>
            원본 대화가 사용자에 의해 삭제되어 <code>sessionId</code> 가 끊긴 쿼리 (고아) 만
            대상입니다. 살아있는 대화에 연결된 쿼리는 영향을 받지 않습니다.
          </p>
          {filtersActive ? (
            <div className="rounded-md border border-border bg-surface-muted/40 p-3 text-xs">
              <div className="font-medium mb-1">현재 필터</div>
              <ul className="space-y-0.5 font-mono">
                {filterSnapshot.ownerEmail ? <li>· 이메일 ~ {filterSnapshot.ownerEmail}</li> : null}
                {filterSnapshot.kind ? <li>· 유형 = {filterSnapshot.kind}</li> : null}
                {filterSnapshot.from ? <li>· 시작 ≥ {filterSnapshot.from}</li> : null}
                {filterSnapshot.to ? <li>· 끝 ≤ {filterSnapshot.to}</li> : null}
              </ul>
            </div>
          ) : (
            <p className="text-foreground-muted">
              필터가 없으므로 <strong>전체 고아 쿼리</strong> 가 대상입니다.
            </p>
          )}
          <p className="text-[12px] text-foreground-muted">
            한 번에 최대 1000건. 더 많으면 재호출로 이어서 삭제할 수 있습니다.
          </p>
        </div>
      )}
    </Modal>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: input 자식이 children 으로 전달되는 wrapping label 패턴 (HTML 적법)
    <label className="flex flex-col gap-1">
      <span className="text-xs text-foreground-muted">{label}</span>
      {children}
    </label>
  );
}
